import { FC } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Divider, Theme, Tooltip, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { FaMinus } from 'react-icons/fa';
import CustomLink from '../../../elements/Aggregation/elements/Link';
import OwnerCell from '../../../elements/TableCells/OwnerCell';
import IssueCardDescription from '../../common/components/IssueCard/IssueCardDescription';
import IssueCardRecommendation from '../../common/components/IssueCard/IssueCardRecommendation';
import IssueCardSeverity from '../../common/components/IssueCard/IssueCardSeverity';
import IssueSummarySection from '../../common/components/IssueCard/IssueSummarySection';
import ScoreWithLabel from '../../common/components/IssueItem/ScoreWithLabel';
import AppFlowsIssues from '../../common/components/IssuesAppFlow/AppFlowsIssues';
import { resolveRiskLevel } from '../../common/utils/severity-utils';
import { IssueDetails } from '../../common/types/issues-types';
import { CategoryDisplayName } from '@oxappsec/ox-consolidated-categories';
import { resolveIssueCategoryIcon } from '../../common/utils/issue-utils';

const PipelineIssueSummary: FC<
  React.PropsWithChildren<PipelineIssueSummaryProps>
> = props => {
  const { classes } = useStyles();
  const { issueDetails } = props;
  const CategoryIcon = resolveIssueCategoryIcon(
    issueDetails.category as unknown as CategoryDisplayName,
  );
  const appScore =
    issueDetails.app.businessPriority === 0
      ? '0'
      : issueDetails.app.businessPriority < 1
      ? '<1'
      : issueDetails.app.businessPriority.toFixed(0);

  return (
    <>
      <div className={classes.twoCols}>
        <IssueCardSeverity
          severity={issueDetails.severity}
          originalSeverity={issueDetails.originalToolSeverity}
          severityBreakdown={issueDetails.severityBreakdown}
        />
      </div>
      <Divider sx={{ mb: 1, mt: 2 }} />
      <div className={classes.twoCols}>
        {issueDetails.secondTitleMarkup && (
          <div>
            <IssueCardDescription learnMoreUrl={issueDetails.learnMore[0]}>
              {issueDetails.secondTitleMarkup}
            </IssueCardDescription>
          </div>
        )}
        {issueDetails.recommendationMarkup &&
          issueDetails.secondTitleMarkup && (
            <Divider sx={{ mx: 2 }} orientation='vertical' flexItem />
          )}
        {issueDetails.recommendationMarkup && (
          <div>
            <IssueCardRecommendation
              fixLink={issueDetails.fixLink}
              issueId={issueDetails.issueId}
              prAvailable={issueDetails.isPRAvailable}>
              {issueDetails.recommendationMarkup}
            </IssueCardRecommendation>
          </div>
        )}
      </div>
      <Divider sx={{ mb: 2, mt: 2 }} />
      <div className={classes.summaryContainer}>
        <IssueSummarySection title='Business Priority & App' width={300}>
          <ScoreWithLabel
            value={appScore}
            severityType={resolveRiskLevel(appScore as unknown as number)}
            label={issueDetails.app.name}
            appLink={issueDetails.app.id}
            buisnessPriority
          />
        </IssueSummarySection>

        <IssueSummarySection title='Job' width={250}>
          <CustomLink
            value={issueDetails.cicdFields?.jobId || ''}
            href={issueDetails.cicdFields?.jobUrl}
          />
        </IssueSummarySection>

        <IssueSummarySection title='Issue Owner' width={200}>
          <OwnerCell owners={issueDetails.owners} />
        </IssueSummarySection>

        <IssueSummarySection title='Pipeline Date' width={200}>
          <Tooltip
            arrow
            placement='top'
            title={
              issueDetails.cicdFields?.jobTriggeredAt as unknown as string
            }>
            <Typography variant='body2' color='text.primary'>
              {issueDetails.cicdFields?.jobTriggeredAt ? (
                formatDistanceToNow(
                  new Date(issueDetails.cicdFields.jobTriggeredAt),
                  {
                    addSuffix: true,
                  },
                )
              ) : (
                <FaMinus />
              )}
            </Typography>
          </Tooltip>
        </IssueSummarySection>

        <IssueSummarySection title='Occurrences' width={150}>
          <Typography variant='body2' color='text.primary'>
            {issueDetails.occurrences}
          </Typography>
        </IssueSummarySection>

        <IssueSummarySection title='Category' width={250}>
          <div className={classes.category}>
            {CategoryIcon && <CategoryIcon className={classes.categoryIcon} />}
            <Typography variant='body2' color='text.primary'>
              {issueDetails.category.name}
            </Typography>
          </div>
        </IssueSummarySection>
      </div>

      <Divider sx={{ mb: 3, mt: 2 }} />
      <Typography variant='body2' color='text.secondary' mb={3}>
        Application Flow
      </Typography>
      <AppFlowsIssues flow={issueDetails.app.applicationFlows} iconSize={30} />
    </>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  twoCols: {
    display: 'flex',
    '& > div': {
      flex: '1 1 100%',
    },
  },
  categoryIcon: {
    width: '1em',
    height: '1em',
    flexShrink: 0,
    color: theme.palette.primary.main,
  },
  summaryContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
  },
  category: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

export interface PipelineIssueSummaryProps {
  issueDetails: IssueDetails;
}

export default PipelineIssueSummary;
